/**
 * xAI Provider Implementation
 * 
 * Implements the IAIProvider interface for xAI's Grok API.
 * Supports models like Grok 3 Mini, Grok 4, Grok 4 Fast.
 * 
 * xAI's API is very similar to OpenAI's API format:
 * - Uses Bearer token authentication
 * - Same message format with system/user/assistant roles
 * - Same response format with choices array
 * - Supports JSON mode with response_format
 */

import {
  AIProvider,
  AIModelConfig,
  AIMessage,
  AIRequestOptions,
  AIResponse
} from '../AIProvider.types';
import { BaseAIProvider } from '../BaseAIProvider';
import { XAI_MODELS } from '../config/modelConfig';
import { PROVIDER_ENDPOINTS } from '../config/providerConfig';
import { ApiClient } from '../../ApiClient';

/**
 * xAI API response format (same as OpenAI)
 */
interface XAIResponse {
  id: string;
  object: string;
  created: number;
  model: string;
  choices: Array<{
    index: number;
    message: {
      role: string;
      content: string;
    };
    finish_reason: string;
  }>;
  usage: {
    prompt_tokens: number;
    completion_tokens: number;
    total_tokens: number;
  };
}

/**
 * xAI message format (same as OpenAI)
 */
interface XAIMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

/**
 * xAI Provider
 * 
 * Handles all communication with xAI's Grok API, including:
 * - Chat completions with Grok models
 * - JSON mode
 * - Token management
 * 
 * The implementation is nearly identical to OpenAI due to API compatibility.
 */
export class XAIProvider extends BaseAIProvider {
  readonly provider = AIProvider.XAI;
  
  /**
   * Get the xAI API token from Forge
   */
  protected async getAuthToken(): Promise<string> {
    return await this.invokeResolver<string>('getXaiApiToken');
  }
  
  /**
   * Generate a completion using xAI's API
   */
  async generateCompletion(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Promise<AIResponse> {
    this.validateRequestOptions(options);
    
    try {
      const token = await this.getAuthToken();
      
      const payload = this.buildRequestPayload(messages, options);
      
      const response = await ApiClient.post(
        PROVIDER_ENDPOINTS[AIProvider.XAI],
        payload,
        { Authorization: `Bearer ${token}` }
      ) as XAIResponse;
      
      return this.normalizeResponse(response);
    } catch (error) {
      this.handleError(error);
    }
  }
  
  /**
   * Build the request payload for xAI's API
   * Format is compatible with OpenAI
   */
  private buildRequestPayload(
    messages: AIMessage[],
    options: AIRequestOptions
  ): Record<string, unknown> {
    const formattedMessages = this.formatMessages(messages, options.systemPrompt);
    
    const payload: Record<string, unknown> = {
      model: this.getApiModelName(options.model),
      temperature: options.temperature ?? 0,
      messages: formattedMessages
    };
    
    // Add max_tokens if specified
    if (options.maxTokens) {
      payload.max_tokens = options.maxTokens;
    }
    
    // Add structured output if requested
    if (options.jsonMode) {
      payload.response_format = {
        type: 'json_schema',
        json_schema: {
          name: 'structured_output',
          strict: true,
          schema: {
            type: 'object',
            additionalProperties: true
          }
        }
      };
    }
    
    return payload;
  }
  
  /**
   * Get the API model name from the model ID
   */
  private getApiModelName(modelId: string): string {
    const model = XAI_MODELS.find(m => m.id === modelId);
    return model?.name || modelId;
  }
  
  /**
   * Format messages for xAI's API
   * xAI supports system messages directly in the messages array (like OpenAI)
   */
  private formatMessages(
    messages: AIMessage[],
    systemPrompt?: string
  ): XAIMessage[] {
    const formatted: XAIMessage[] = [];
    
    // Add system prompt if provided
    if (systemPrompt) {
      formatted.push({
        role: 'system',
        content: systemPrompt
      });
    }
    
    // Add conversation messages
    for (const msg of this.normalizeMessages(messages)) {
      formatted.push({
        role: msg.role,
        content: msg.content
      });
    }
    
    return formatted;
  }
  
  /**
   * Normalize xAI's response to our standard format
   * Response format is compatible with OpenAI
   */
  private normalizeResponse(response: XAIResponse): AIResponse {
    const choice = response.choices[0];
    
    return {
      content: choice?.message?.content || '',
      model: response.model,
      provider: this.provider,
      usage: {
        promptTokens: response.usage?.prompt_tokens || 0,
        completionTokens: response.usage?.completion_tokens || 0,
        totalTokens: response.usage?.total_tokens || 0
      },
      finishReason: choice?.finish_reason || 'stop'
    };
  }
  
  /**
   * Check if xAI provider is available
   */
  async isAvailable(): Promise<boolean> {
    try {
      const token = await this.getAuthToken();
      return !!token && token.length > 0;
    } catch {
      return false;
    }
  }
  
  /**
   * Get available xAI models
   */
  getAvailableModels(): AIModelConfig[] {
    return XAI_MODELS;
  }
}
